import React, { FC, ReactNode } from 'react'
import classNames from 'classnames'
import Color from 'color'
import { makeStyles, Box, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { TIntegrationType } from 'apps/fleet-planner/types'
import { useColorStyles } from 'transfix-ui'
import { EldProviderLogoImage } from '../EldProviderList'
import { EldProviderRowFeatures, IEldProviderFeature } from './EldProvierRowFeatures'

const useEldProviderLabelStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    minHeight: 157,
    background: brandColors.white,
    border: `1px solid ${brandColors.coolGray4}`,
    borderRadius: theme.shape.borderRadius,

    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  selected: {
    background: Color(brandColors.skyBlue2).mix(Color(brandColors.white), 0.5).hex(),
    border: `1px solid ${brandColors.skyBlue6}`,
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },

    '&:first-child': {
      [theme.breakpoints.up('md')]: {
        borderRight: `1px solid ${brandColors.coolGray4}`,
      },
    },

    '$selected &': {
      '&:first-child': {
        [theme.breakpoints.up('md')]: {
          borderRight: `1px solid ${brandColors.skyBlue6}`,
        },
      },
    },
  },
}))

interface IEldProviderLabelProps {
  /**
   * @example 'keepTruckin'
   */
  logoName: TIntegrationType
  /**
   * @example 'KeepTruckin'
   */
  title: ReactNode
  /**
   * @example 'Connects with 750+ ELD providers'
   */
  subtitle?: ReactNode
  /**
   * @example 'Free'
   */
  cost?: string
  /**
   * Array of features which are mapped/rendered
   */
  features: IEldProviderFeature[]
  /**
   * @default false
   */
  selected?: boolean
}

/**
 * Used to style the <Radio /> element's label for ELD Providers
 */
export const EldProviderLabel: FC<IEldProviderLabelProps> = ({
  logoName,
  title,
  subtitle,
  features = [],
  cost = 'Free',
  selected = false,
}) => {
  const colors = useColorStyles()
  const classes = useEldProviderLabelStyles()
  return (
    <Box
      className={classNames(classes.root, { [classes.selected]: selected })}
      data-test='eld-provider-label'>
      <Box className={classes.cell} flex='0 0 128px' justifyContent='center'>
        <EldProviderLogoImage logoName={logoName} />
      </Box>
      <Box className={classes.cell} flex='0 0 160px' pr={0}>
        <Box>
          <Typography variant='h3' component='div'>
            {title}
          </Typography>
          {subtitle && (
            <Box pt={0.5}>
              <Typography variant='subtitle2' component='div'>
                {subtitle}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box className={classes.cell} flex='1 1 auto' pr={0}>
        <EldProviderRowFeatures features={features} />
      </Box>
      <Box className={classes.cell} flex='0 0 100px'>
        <Typography variant='h3' className={colors.skyBlue6}>
          {cost}
        </Typography>
      </Box>
    </Box>
  )
}
